import { FormikProps } from "formik";
import { useEffect, useState } from "react";
import { Row, Col } from "reactstrap";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import { SetState } from "../../../assets/types";
import { DrawerFormSaveResult, hasClientErrorOperationStatusCode } from "../../../assets/utils/lib";
import {
    ClaimHelper,
    StringHelper,
} from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { ImageFileDto } from "../../../assets/utils/lib/imageFileDto";
import { LookupDto } from "../../../assets/utils/lib/lookupDto";
import { DropdownOption } from "../../../assets/vfx/controls/components/dropdown/controlDropdown";
import Drawer from "../../../assets/vfx/drawer/drawer";
import Form from "../../../assets/vfx/form/form";
import { ReferenceDto } from "../../../services/dto";
import { HEX_COLOR_REGEX, normalizeHexColor } from "../../common/HexColorSelector";
import { LINK_TYPE } from "./referenceContainer";
import { BackgroundStyleToggle } from "./referenceForm.styled";

enum TileBackground {
    Image = "image",
    Icon = "icon",
}

const ReferenceColorDefaults = {
    parentIcon: "var(--color-icon-parent-accent)",
    childIcon: "var(--color-icon-muted)",
    background: "var(--color-background)",
    transparent: "var(--color-transparent)",
};

interface ReferenceFormProps {
    loading?: boolean;
    data?: ReferenceDto;
    show: boolean;
    parentId?: string;
    communityId?: string;
    linkType?: string;
    linkTypeId?: string;
    lookups: {
        communities: LookupDto[];
        pageLinks: LookupDto[];
    };
    references?: ReferenceDto[];
    parent?: ReferenceDto;
    totalNumberOfItems: number | undefined;
    showProxy: boolean;
    onHide: () => void;
    onSubmit: (values: ReferenceDto) => DrawerFormSaveResult;
    setCommunityId: SetState<string | undefined>;
}

const hexColorValidation = yup
    .string()
    .nullable()
    .notRequired()
    .matches(HEX_COLOR_REGEX, "Use hex format #RRGGBB");

const validationSchema = yup.object().shape({
    communityId: yup.string().required("Community is required."),
    display: yup.string().required("Display is required."),
    description: yup.string().nullable(),
    iconBackgroundColor: hexColorValidation,
    textColor: yup.string().nullable(),
    iconForecolor: hexColorValidation,
    iconType: yup.string().nullable(),
    iconName: yup
        .string()
        .nullable()
        .when("tileImages", ([tileImages], schema) =>
            tileImages === TileBackground.Icon
                ? schema.required("Please select an icon.")
                : schema
        ),
    image: yup
        .mixed()
        .nullable()
        .when("tileImages", ([tileImages], schema) =>
            tileImages === TileBackground.Image
                ? schema.required("Please select an image.")
                : schema
        ),
    tileImages: yup.string().nullable(),
    backgroundImage: yup.mixed().nullable(),
    headerImage: yup.mixed().nullable(),
    url: yup.string().nullable(),
    pageLinkId: yup.string().nullable(),
    proxyId: yup.string().nullable(),
    sortValue: yup.number().required("Sort Value is required."),
});

const getSubmitFieldError = (
    formikProps: FormikProps<ReferenceDto>,
    name: keyof ReferenceDto
): string | undefined => {
    const error = formikProps.errors[name];
    const touched = formikProps.touched[name];
    const shouldShowError = touched || formikProps.submitCount > 0;

    return shouldShowError && typeof error === "string" ? error : undefined;
};

export const ReferenceForm = (props: ReferenceFormProps) => {
    const { communityId: paramCommunityId } = useParams();
    const claimCommunityId = ClaimHelper.getCommunityId();
    const communityId = props.communityId || paramCommunityId || claimCommunityId;

    const [title, setTitle] = useState<string>("");
    const [hasParent, setHasParent] = useState<boolean>(false);
    const [imageError, setImageError] = useState<string | null | undefined>(null);
    const [isParent, setIsParent] = useState<boolean>(false);
    const [tileImageType, setTileImageType] = useState<TileBackground>(
        !!props.data?.image
            ? TileBackground.Image
            : !!props.data?.iconName
                ? TileBackground.Icon
                : TileBackground.Image
    );
    type referenceTypeOptions = "url" | "parent";
    const [referenceType, setReferenceType] = useState<
        referenceTypeOptions | undefined
    >();

    const setFormTitle = (isNew: boolean): string => {
        const actionText = isNew ? "New" : "Edit";

        const hasParent =
            props.parentId &&
            props.parentId !== Constants.emptyGuid;

        if (hasParent && props.parent?.display) {
            return `${props.parent.display} - ${actionText} Item`;
        }

        if (hasParent && props.data?.parentName) {
            return `${props.data?.parentName} - ${actionText} Item`;
        }

        // child item
        const isReferenceItem =
            props.linkTypeId?.toLowerCase() ===
            LINK_TYPE.REFERENCE_ITEM.toLowerCase();

        const typeText = isReferenceItem ? "Resource" : props.linkType;

        return `${actionText} ${typeText}`;
    };

    useEffect(() => {
        setHasParent(
            props.parentId === undefined ||
            props.parentId === null ||
            props.parentId === Constants.emptyGuid
        );
        setTileImageType(!!props.data?.image ? TileBackground.Image : !!props.data?.iconName ? TileBackground.Icon : TileBackground.Image)
        const isNew: boolean =
            StringHelper.whenNullOrEmpty(props.data?.id, Constants.emptyGuid) ===
            Constants.emptyGuid;
        const isParentValue: boolean =
            !isNew &&
            props.references != null &&
            props.references !== undefined &&
            props.references?.some((x) => x.parentId === props.data?.id);
        setIsParent(isParentValue);
        const isUrl = StringHelper.isNotNullOrEmpty(props.data?.url);
        const isPage: boolean = StringHelper.isNotNullOrEmpty(
            props.data?.pageLinkId
        );
        const referenceTypeValue: referenceTypeOptions | undefined = isParentValue
            ? "parent"
            : isUrl
                ? "url"
                : undefined;
        setReferenceType(referenceTypeValue);
        setTitle(setFormTitle(isNew));
    }, [
        props.parentId,
        props.data?.id,
        props.data?.url,
        props.data?.pageLinkId,
        props.linkType,
        props.references,
        props.data?.parentId,
        props.parent?.id,
    ]);

    const referenceTypeOptions: DropdownOption[] = [
        {
            key: "parent",
            value: "parent",
            display: "Parent",
        },
        //{
        //    key: "page",
        //    value: "page",
        //    display: "Page"
        //},
        {
            key: "url",
            value: "url",
            display: "External site",
        },
    ];
    const handleSubmit = (formData: ReferenceDto) => {
        if (!hasParent) formData.parentId = props.parentId;
        formData.pageLinkId =
            formData.pageLinkId && formData.pageLinkId != ""
                ? formData.pageLinkId
                : undefined;
        const normalizedFormData = {
            ...formData,
            iconBackgroundColor: formData.iconBackgroundColor || null,
        } as ReferenceDto;

        return Promise.resolve(props.onSubmit({
            ...normalizedFormData,
        })).then((result) => {
            if (hasClientErrorOperationStatusCode(result)) {
                return;
            }

            props.onHide();
        });
    };

    const isNew = !props.data?.id || props.data?.id === Constants.emptyGuid;

    const eq = (a?: string | null, b?: string | null) =>
        (a ?? "").toLowerCase() === (b ?? "").toLowerCase();

    const siblingsCount = (props.references ?? []).filter((r) => {
        const sameParent = props.parentId
            ? r.parentId === props.parentId
            : r.parentId == null;
        const sameList = props.linkTypeId
            ? eq(r.linkTypeId, props.linkTypeId)
            : true;
        const sameCommunity = eq(r.communityId, communityId);
        return sameParent && sameList && sameCommunity;
    }).length;

    const defaultSort = isNew ? siblingsCount + 1 : siblingsCount;

    const getPreviewBackgroundColor = (values: ReferenceDto): string =>
        values.iconBackgroundColor
            ? normalizeHexColor(values.iconBackgroundColor, ReferenceColorDefaults.background)
            : ReferenceColorDefaults.transparent;

    const getDefaultForegroundColor = (): string =>
        props.data?.iconForecolor ??
        props.parent?.iconForecolor ??
        (!props.parentId || props.parentId === Constants.emptyGuid
            ? ReferenceColorDefaults.parentIcon
            : ReferenceColorDefaults.childIcon);

    const getPreviewForegroundColor = (values: ReferenceDto): string =>
        normalizeHexColor(values.iconForecolor, getDefaultForegroundColor());

    const renderReferenceTypeFields = (
        formikProps: FormikProps<ReferenceDto>
    ) => {
        if (props.showProxy) {
            return (
                <Form.FieldRow label="Reference link">
                    <Form.Autocomplete
                        name="proxyId"
                        id="proxyId"
                        labelKey="name"
                        placeholder="Select reference link"
                        options={
                            !Array.isArray(props.references) || props.references.length === 0
                                ? []
                                : props.references.map((reference) => ({
                                    id: reference.id as string,
                                    name: reference.display as string,
                                }))
                        }
                    />
                </Form.FieldRow>
            );
        }

        return (
            <>
                <Form.SelectRow
                    name="referenceType"
                    label="Reference type"
                    disabled={isParent || props.loading}
                    value={referenceType ?? ""}
                    blankOption={{
                        key: Constants.emptyGuid,
                        value: "",
                        display: "Select reference type",
                    }}
                    options={referenceTypeOptions}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        switch (e.target.value.toLowerCase()) {
                            case "parent":
                                formikProps.setFieldValue("pageLinkId", "");
                                formikProps.setFieldValue("url", "");
                                setReferenceType("parent");
                                break;
                            case "url":
                                formikProps.setFieldValue("pageLinkId", "");
                                setReferenceType("url");
                                break;
                            //case "page":
                            //    formikProps.setFieldValue("url", '');
                            //    setReferenceType("page")
                            //    break;
                            default:
                                formikProps.setFieldValue("pageLinkId", "");
                                formikProps.setFieldValue("url", "");
                                setReferenceType("parent");
                                break;
                        }
                    }}
                />
                <Form.TextRow
                    name="url"
                    label="External URL"
                    placeholder="Enter text"
                    hidden={referenceType !== "url"}
                />
                <Form.Dropdown
                    name="pageLinkId"
                    label="Page"
                    isLoading={props.loading}
                    blankOption={{
                        key: Constants.emptyGuid,
                        value: Constants.emptyGuid,
                        display: "Select page",
                    }}
                    options={LookupDto.mapToOptions(props.lookups?.pageLinks ?? [])}
                    isHidden={true}
                />
            </>
        );
    };

    const validateImageDimensions = (file: ImageFileDto): Promise<boolean> => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const valid = img.width <= 125 && img.height <= 125;
                resolve(valid);
            };
            img.onerror = () => {
                resolve(false);
            };
            img.src = `data:${file.mimeType};base64,${file.content}`;
        });
    };

    const handleFormSubmit = async (values: ReferenceDto) => {
        if (tileImageType === TileBackground.Image) {
            if (!values.image) {
                setImageError("Please select an image.");
                return;
            }

            const isValidDimensions = await validateImageDimensions(
                values.image
            );

            if (!isValidDimensions) {
                setImageError("Image dimensions must be 125 by 125 or smaller.");
                return;
            }

            setImageError(null);
            return handleSubmit({
                ...values,
                iconName: undefined,
                iconType: undefined,
            });
        }

        if (!values.iconType) {
            setImageError("Please select an icon.");
            return;
        }

        setImageError(null);
        return handleSubmit({ ...values, image: undefined });
    };

    return (
        <Drawer.Form
            className="w-40"
            show={props.show}
            backdrop
            placement="end"
            scroll
            title={title}
            initialValues={{
                sortValue: defaultSort,
                ...props.data,
                iconBackgroundColor: props.data?.iconBackgroundColor ?? null,
                iconForecolor: props.data?.iconForecolor ?? getDefaultForegroundColor(),
                communityId: communityId,
                displayHeaderText: props.data?.displayHeaderText ?? false,
                isDisplayed: props.data?.isDisplayed ?? true,
                tileImages: !!props.data?.image ? TileBackground.Image : !!props.data?.iconName ? TileBackground.Icon : TileBackground.Image
            }}
            validationSchema={validationSchema}
            enableReinitialize
            on_Submit={handleFormSubmit}
            onHide={props.onHide}
        >
            {(formikProps: FormikProps<ReferenceDto>) => (
                <>
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.PanelSection
                                iconClassName="bi bi-file-earmark-text"
                                title="1. Basic information"
                            >
                                <Form.Dropdown
                                    name="communityId"
                                    label="Community"
                                    disabled={!!communityId && communityId !== Constants.emptyGuid}
                                    isLoading={props.loading}
                                    blankOption={{
                                        key: Constants.emptyGuid,
                                        value: Constants.emptyString,
                                        display: "Select community",
                                    }}
                                    options={LookupDto.mapToOptions(props.lookups?.communities ?? [])}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                        props.setCommunityId(e.target.value)
                                    }
                                    isHidden={true}
                                />
                                <Form.TextRow
                                    name="display"
                                    label="Display name"
                                    placeholder="Enter text"
                                    required
                                />
                                <Form.TextRow
                                    name="description"
                                    label="Description"
                                    placeholder="Enter text"
                                    rows={3}
                                />
                                {renderReferenceTypeFields(formikProps)}
                                <Form.SwitchRow
                                    name="isDisplayed"
                                    label="Visible to users"
                                    checkLabel="Yes, display this resource"
                                />
                                <Form.NumberRow
                                    name="sortValue"
                                    label="Display order"
                                    min={1}
                                    max={defaultSort}
                                    required
                                />
                            </Form.PanelSection>

                            <Form.PanelSection
                                iconClassName="bi bi-grid"
                                title="2. Appearance"
                                style={{ marginTop: 16 }}
                            >
                                <Form.FieldRow label="Background style">
                                    <div className="d-flex gap-2">
                                        <BackgroundStyleToggle
                                            type="button"
                                            $active={tileImageType === TileBackground.Image}
                                            aria-pressed={tileImageType === TileBackground.Image}
                                            onClick={() => {
                                                setImageError(null);
                                                setTileImageType(TileBackground.Image);
                                                formikProps.setFieldValue("tileImages", TileBackground.Image);
                                            }}
                                        >
                                            <i className="bi bi-image" />
                                            Image
                                        </BackgroundStyleToggle>
                                        <BackgroundStyleToggle
                                            type="button"
                                            $active={tileImageType === TileBackground.Icon}
                                            aria-pressed={tileImageType === TileBackground.Icon}
                                            onClick={() => {
                                                setImageError(null);
                                                setTileImageType(TileBackground.Icon);
                                                formikProps.setFieldValue("tileImages", TileBackground.Icon);
                                            }}
                                        >
                                            <i className="bi bi-grid-3x3-gap" />
                                            Icon
                                        </BackgroundStyleToggle>
                                    </div>
                                </Form.FieldRow>
                                {tileImageType === TileBackground.Image ? (
                                    <Form.FieldRow
                                        label="Tile image"
                                        required
                                        error={
                                            imageError ??
                                            getSubmitFieldError(formikProps, "image")
                                        }
                                    >
                                        <Form.ImageCrop
                                            name="image"
                                            loading={props.loading}
                                            aspect={1}
                                            maxOutputWidth={125}
                                            previewMaxHeight={125}
                                            modalTitle="Tile image crop"
                                            chooseText="Choose tile image"
                                            changeText="Change image"
                                            onChange={() => {
                                                setImageError(null);
                                            }}
                                        />
                                        <div className="small d-flex fst-italic opacity-75 mt-1">
                                            <span className="me-2 text-primary">*</span>
                                            <span>Recommended size 125 x 125</span>
                                        </div>
                                    </Form.FieldRow>
                                ) : (
                                    <>
                                        <Form.FieldRow
                                            label="Tile icon"
                                            required
                                            error={
                                                imageError ??
                                                getSubmitFieldError(formikProps, "iconName")
                                            }
                                        >
                                            <Form.Icon
                                                nameType="iconType"
                                                nameName="iconName"
                                                isLoading={props.loading}
                                                isHidden={!!isParent}
                                                previewSize={42}
                                                style={{ width: 72, height: 72 }}
                                                iconColor={getPreviewForegroundColor(formikProps.values)}
                                                previewBackgroundColor={getPreviewBackgroundColor(
                                                    formikProps.values
                                                )}
                                                onClick={() => setImageError(null)}
                                            />
                                        </Form.FieldRow>
                                        <Form.ColorRow
                                            name="iconBackgroundColor"
                                            label="Icon background color"
                                            fallbackColor={ReferenceColorDefaults.background}
                                            allowNull
                                        />
                                        <Form.ColorRow
                                            name="iconForecolor"
                                            label="Icon foreground color"
                                            fallbackColor={getDefaultForegroundColor()}
                                        />
                                    </>
                                )}
                            </Form.PanelSection>
                        </Col>
                        <Col xs={12}>
                            <Form.PanelSection
                                iconClassName="bi bi-image"
                                title="3. Header"
                            >
                                <Form.FieldRow label="Header image">
                                    <Form.ImageCrop
                                        name="headerImage"
                                        loading={props.loading}
                                        aspect={16 / 9}
                                        maxOutputWidth={1400}
                                        previewMaxHeight={220}
                                        modalTitle="Header image crop"
                                        chooseText="Choose header image"
                                        changeText="Change image"
                                    />
                                    <div className="small d-flex fst-italic opacity-75 mt-1">
                                        <span className="me-2 text-primary">*</span>
                                        <span>Recommended size 1400 x 788</span>
                                    </div>
                                </Form.FieldRow>
                                <Form.SwitchRow
                                    name="displayHeaderText"
                                    label="Display text in header"
                                    checkLabel="Yes, display text"
                                />
                            </Form.PanelSection>
                        </Col>
                    </Row>
                </>
            )}
        </Drawer.Form>
    );
};